import React, {
   useCallback,
   useContext,
   useEffect,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
   useSyncExternalStore,
} from "react";
import { ReactSVG } from "react-svg";

import {
   ensureStaffConversation,
   getMyConversation,
   getStaffConversation,
   listStaffConversations,
   postMyConversationMessage,
   postStaffConversationMessage,
} from "../../api/chatService";
import { getGroups, getManagerGroups } from "../../api/groupsService";
import { getUsers } from "../../api/usersService";
import { unwrapList, readStudentGroupId } from "../../utils/groupFields";
import { ROLES, normalizeRole } from "../../auth/access";
import { UserContext } from "../../UserContext";
import useChatSocket from "../../hooks/useChatSocket";
import IconButton from "../Common/IconButton";
import StudentItem from "../Common/StudentItem";
import sendIcon from "../../assets/svg/send-chat.png";
import instruireAutoLogo from "../../assets/svg/logo.svg";
import StudentItemInline from "./StudentInfo/StudentItemInline";
import {
   getAvatarColorFromName,
   getInitials,
} from "./StudentInfo/studentInfoShared";
import { setChatToastContext } from "../../stores/chatToastContext";
import { setLastReadChatMessageId } from "../../stores/chatUnreadStore";
import {
   AWAITING_REPLY_SNOOZE_HOURS,
   clearStaffChatAwaitingReply,
   clearStaffChatComposerDraft,
   clearStaffChatThreadSelection,
   countStaffChatAwaitingReply,
   dismissStaffChatAwaitingReply,
   getStaffChatAwaitingReplyState,
   getStaffChatUnreadCount,
   getStaffChatUnreadTotal,
   getStaffChatWorkspaceState,
   incrementStaffChatUnread,
   markStaffChatConversationRead,
   setStaffChatActiveTab,
   setStaffChatSelection,
   setStaffChatView,
   snoozeStaffChatAwaitingReply,
   subscribeStaffChatWorkspace,
   syncStaffChatUnreadFromConversations,
   trackStaffChatReadForReply,
} from "../../stores/staffChatWorkspaceStore";

const MAX_LEN = 2000;
const MOBILE_BP = 768;
const PAGE_SIZE = 16;
const PAGE_SIZE_MOBILE = 4;

function safeText(value) {
   return String(value ?? "").trim();
}

function readStudentName(student) {
   const first = safeText(student?.firstName);
   const last = safeText(student?.lastName);
   const full = `${first} ${last}`.trim();
   return full || safeText(student?.email) || "Student";
}

function formatMessageTime(value) {
   if (!value) return "";
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return "";
   return d.toLocaleString("ro-RO", {
      hour: "2-digit",
      minute: "2-digit",
   });
}

function readMessageDayStart(value) {
   if (!value) return NaN;
   const d = new Date(value);
   if (Number.isNaN(d.getTime())) return NaN;
   return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function formatMessageDay(value) {
   const dayStart = readMessageDayStart(value);
   if (Number.isNaN(dayStart)) return "";

   const d = new Date(dayStart);
   const now = new Date();
   const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
   ).getTime();
   const diffDays = Math.round((todayStart - dayStart) / 86400000);

   if (diffDays === 0) return "Astăzi";
   if (diffDays === 1) return "Ieri";

   if (diffDays > 1 && diffDays < 7) {
      const weekday = d.toLocaleDateString("ro-RO", { weekday: "long" });
      return weekday.charAt(0).toUpperCase() + weekday.slice(1);
   }

   if (d.getFullYear() === now.getFullYear()) {
      return d.toLocaleDateString("ro-RO", { day: "numeric", month: "long" });
   }

   return d.toLocaleDateString("ro-RO", {
      day: "numeric",
      month: "long",
      year: "numeric",
   });
}

function isStaffRole(role) {
   const r = normalizeRole(role);
   return r === ROLES.ADMIN || r === ROLES.MANAGER;
}

function isOwnStudentMessage(message, userId) {
   const role = safeText(message?.senderRole).toUpperCase();
   if (role === "STUDENT" || role === "USER") return true;
   return Number(message?.senderId) === Number(userId);
}

function isStaffMessage(message) {
   const role = safeText(message?.senderRole).toUpperCase();
   return role === "MANAGER" || role === "ADMIN";
}

function isStudentMessage(message) {
   const role = safeText(message?.senderRole).toUpperCase();
   return role === "STUDENT" || role === "USER";
}

function markConversationReadAndTrack(conversationId, lastMessage) {
   if (conversationId == null) return;
   markStaffChatConversationRead(conversationId, lastMessage?.id);
   if (isStudentMessage(lastMessage)) {
      trackStaffChatReadForReply(conversationId, lastMessage);
   } else {
      clearStaffChatAwaitingReply(conversationId);
   }
}

function AwaitingReplyPill({ conversationId, messageId, onAction }) {
   const [open, setOpen] = useState(false);
   const wrapRef = useRef(null);

   useEffect(() => {
      if (!open) return undefined;
      const onDoc = (event) => {
         if (!wrapRef.current?.contains(event.target)) setOpen(false);
      };
      document.addEventListener("mousedown", onDoc);
      return () => document.removeEventListener("mousedown", onDoc);
   }, [open]);

   return (
      <span
         className="staffChatPopup__awaitingWrap"
         ref={wrapRef}
         onClick={(event) => event.stopPropagation()}
         onKeyDown={(event) => event.stopPropagation()}
      >
         <button
            type="button"
            className={
               "staffChatPopup__awaitingPill" + (open ? " is-open" : "")
            }
            title="Citit, fără răspuns — opțiuni"
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={() => setOpen((v) => !v)}
         >
            Răspunzi?
         </button>
         {open ? (
            <span className="staffChatPopup__awaitingMenu" role="menu">
               <button
                  type="button"
                  role="menuitem"
                  className="staffChatPopup__awaitingMenuItem"
                  onClick={() => {
                     snoozeStaffChatAwaitingReply(
                        conversationId,
                        AWAITING_REPLY_SNOOZE_HOURS,
                     );
                     setOpen(false);
                     onAction?.();
                  }}
               >
                  Amintește-mi peste {AWAITING_REPLY_SNOOZE_HOURS}h
               </button>
               <button
                  type="button"
                  role="menuitem"
                  className="staffChatPopup__awaitingMenuItem"
                  onClick={() => {
                     dismissStaffChatAwaitingReply(conversationId, messageId);
                     setOpen(false);
                     onAction?.();
                  }}
               >
                  Ascunde până la mesaj nou
               </button>
            </span>
         ) : null}
      </span>
   );
}

function readConversationStudentId(conv) {
   const raw =
      conv?.student?.id ??
      conv?.studentId ??
      conv?.student_id ??
      conv?.user?.id ??
      conv?.userId ??
      null;
   const id = Number(raw);
   return Number.isFinite(id) && id > 0 ? id : null;
}

function mergeMessageLists(prev, incoming) {
   const map = new Map();
   for (const item of prev || []) {
      if (item?.id != null) map.set(String(item.id), item);
   }
   for (const item of incoming || []) {
      if (item?.id != null) map.set(String(item.id), item);
   }
   return Array.from(map.values()).sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
   );
}

function useIsMobile(bp = MOBILE_BP) {
   const [isMobile, setIsMobile] = useState(() => {
      if (typeof window === "undefined") return false;
      return window.matchMedia(`(max-width:${bp}px)`).matches;
   });

   useEffect(() => {
      if (typeof window === "undefined") return undefined;

      const mq = window.matchMedia(`(max-width:${bp}px)`);
      const onChange = (event) => setIsMobile(event.matches);

      if (mq.addEventListener) mq.addEventListener("change", onChange);
      else mq.addListener(onChange);

      return () => {
         if (mq.removeEventListener) mq.removeEventListener("change", onChange);
         else mq.removeListener(onChange);
      };
   }, [bp]);

   return isMobile;
}

function readStaffSenderLabel(message) {
   if (isAutomatedMessage(message)) return "Notificare automată";
   const name = safeText(message?.senderName);
   if (name) return name;
   return "";
}

// Mementourile trimise de sistem (monitorizarea programării): backend-ul le
// marchează cu `isAutomated`; pentru personal vin și cu numele „Mesaj automat".
function isAutomatedMessage(message) {
   return (
      message?.isAutomated === true ||
      safeText(message?.senderName) === "Mesaj automat"
   );
}

function ChatMessageList({ messages, viewerRole, userId, scrollRef }) {
   const bottomRef = useRef(null);

   useEffect(() => {
      const scrollEl = scrollRef?.current;
      if (!scrollEl) return;
      scrollEl.scrollTop = scrollEl.scrollHeight;
   }, [messages, scrollRef]);

   return (
      <div className="staffChatPopup__messages">
         <div className="staffChatPopup__messagesSpacerTop" aria-hidden="true" />
         {!messages?.length ? (
            <p className="staffChatPopup__empty">Niciun mesaj încă.</p>
         ) : (
            messages.map((message, index) => {
               const own = isStaffRole(viewerRole)
                  ? isStaffMessage(message)
                  : isOwnStudentMessage(message, userId);
               const time = formatMessageTime(message.createdAt);
               const dayStart = readMessageDayStart(message.createdAt);
               const prevDayStart = readMessageDayStart(
                  messages[index - 1]?.createdAt,
               );
               const dayLabel =
                  !Number.isNaN(dayStart) && dayStart !== prevDayStart
                     ? formatMessageDay(message.createdAt)
                     : "";
               const senderLabel =
                  isStaffRole(viewerRole) && isStaffMessage(message)
                     ? readStaffSenderLabel(message)
                     : "";
               const notice = isAutomatedMessage(message);
               return (
                  <React.Fragment key={message.id}>
                     {dayLabel ? (
                        <div
                           className="staffChatPopup__dayDivider"
                           role="separator"
                        >
                           <span className="staffChatPopup__dayDividerLabel">
                              {dayLabel}
                           </span>
                        </div>
                     ) : null}
                     <div
                        className={
                           "staffChatPopup__bubbleCol" +
                           (own
                              ? " staffChatPopup__bubbleCol--own"
                              : " staffChatPopup__bubbleCol--other")
                        }
                     >
                        {senderLabel ? (
                           <span className="staffChatPopup__senderPill">
                              {senderLabel}
                           </span>
                        ) : null}
                        <div
                           className={
                              "staffChatPopup__bubble" +
                              (own
                                 ? " staffChatPopup__bubble--own"
                                 : " staffChatPopup__bubble--other") +
                              (notice ? " staffChatPopup__bubble--notice" : "")
                           }
                           role={notice ? "note" : undefined}
                        >
                           <p className="staffChatPopup__bubbleBody">
                              {message.body}
                              <span
                                 className={
                                    "staffChatPopup__bubbleTime" +
                                    (notice ? " staffChatPopup__bubbleTime--notice" : "")
                                 }
                              >
                                 {notice ? (
                                    <span className="staffChatPopup__noticeTag">
                                       Notificare
                                    </span>
                                 ) : null}
                                 {notice && time ? " · " : ""}
                                 {time}
                              </span>
                           </p>
                        </div>
                     </div>
                  </React.Fragment>
               );
            })
         )}
         <div
            className="staffChatPopup__messagesSpacerBottom"
            aria-hidden="true"
         />
         <div ref={bottomRef} aria-hidden="true" />
      </div>
   );
}

function ChatThreadBody({
   head,
   headClassName = "",
   error,
   loading,
   messages,
   viewerRole,
   userId,
   composer,
}) {
   const threadRef = useRef(null);
   const headRef = useRef(null);
   const composerRef = useRef(null);
   const scrollRef = useRef(null);

   const syncOffsets = useCallback(() => {
      const thread = threadRef.current;
      if (!thread) return;
      thread.style.setProperty(
         "--chat-head-h",
         `${headRef.current?.offsetHeight ?? 0}px`,
      );
      thread.style.setProperty(
         "--chat-compose-h",
         `${composerRef.current?.offsetHeight ?? 0}px`,
      );
   }, []);

   useLayoutEffect(() => {
      syncOffsets();
      const observer = new ResizeObserver(syncOffsets);
      if (headRef.current) observer.observe(headRef.current);
      if (composerRef.current) observer.observe(composerRef.current);
      return () => observer.disconnect();
   }, [syncOffsets, head, composer, loading, messages?.length]);

   return (
      <div className="staffChatPopup__thread" ref={threadRef}>
         <div className="staffChatPopup__threadScroll" ref={scrollRef}>
            {error ? <p className="staffChatPopup__error">{error}</p> : null}
            {loading ? (
               <p className="staffChatPopup__state">Se încarcă...</p>
            ) : (
               <ChatMessageList
                  messages={messages}
                  viewerRole={viewerRole}
                  userId={userId}
                  scrollRef={scrollRef}
               />
            )}
         </div>

         <div
            ref={headRef}
            className={`staffChatPopup__threadHead ${headClassName}`.trim()}
         >
            {head}
         </div>

         {React.isValidElement(composer)
            ? React.cloneElement(composer, { formRef: composerRef })
            : composer}
      </div>
   );
}

function InstruireAutoChatProfile() {
   const [logoFailed, setLogoFailed] = useState(false);
   const appIconSrc = `${process.env.PUBLIC_URL || ""}/android-chrome-192x192.png`;

   return (
      <div className="studentItem staffChatPopup__threadProfile">
         <div className="studentItem__avatar staffChatPopup__brandAvatar">
            {!logoFailed ? (
               <img
                  className="staffChatPopup__brandLogoImg"
                  src={appIconSrc}
                  alt=""
                  aria-hidden="true"
                  onError={() => setLogoFailed(true)}
               />
            ) : (
               <ReactSVG
                  src={instruireAutoLogo}
                  className="staffChatPopup__brandLogoSvg"
                  aria-hidden="true"
               />
            )}
         </div>
         <div className="studentItem__info">
            <h3 className="studentItem__name">Instruire Auto</h3>
            <p className="studentItem__meta">Echipa Instruire Auto</p>
         </div>
      </div>
   );
}

function ChatComposer({
   disabled,
   sending,
   onSend,
   placeholder,
   formRef,
   draftText = "",
   draftToken = 0,
}) {
   const [text, setText] = useState("");
   const inputRef = useRef(null);

   const resizeInput = useCallback(() => {
      const el = inputRef.current;
      if (!el) return;
      el.style.height = "auto";
      const styles = window.getComputedStyle(el);
      const lineHeight = parseFloat(styles.lineHeight) || 18;
      const padY =
         parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
      const maxHeight = lineHeight * 5 + padY;
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
   }, []);

   const focusInput = useCallback(() => {
      requestAnimationFrame(() => {
         inputRef.current?.focus();
      });
   }, []);

   useEffect(() => {
      if (!draftToken) return;
      const next = String(draftText || "");
      if (!next) {
         clearStaffChatComposerDraft();
         return;
      }
      setText(next);
      clearStaffChatComposerDraft();
      focusInput();
   }, [draftToken, draftText, focusInput]);

   useEffect(() => {
      resizeInput();
   }, [text, resizeInput]);

   const handleSubmit = async (event) => {
      event.preventDefault();
      const body = safeText(text);
      if (!body || disabled || sending) return;
      const ok = await onSend(body);
      if (ok) {
         setText("");
         focusInput();
      }
   };

   return (
      <form
         ref={formRef}
         className="staffChatPopup__composer staffChatPopup__composer--absolute"
         onSubmit={handleSubmit}
      >
         <div className="staffChatPopup__inputWrap">
            <textarea
               ref={inputRef}
               className="staffChatPopup__input"
               rows={1}
               maxLength={MAX_LEN}
               value={text}
               placeholder={placeholder}
               disabled={disabled || sending}
               onChange={(event) => setText(event.target.value)}
               onInput={resizeInput}
               onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                     event.preventDefault();
                     handleSubmit(event);
                  }
               }}
            />
         </div>
         <button
            type="submit"
            className="staffChatPopup__send"
            disabled={disabled || sending || !safeText(text)}
            aria-label="Trimite mesajul"
            title="Trimite"
         >
            <img
               className="staffChatPopup__sendIcon"
               src={sendIcon}
               alt=""
               aria-hidden="true"
            />
         </button>
      </form>
   );
}

function StudentChatView() {
   const { user } = useContext(UserContext);
   const [conversation, setConversation] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [sending, setSending] = useState(false);

   const load = useCallback(async () => {
      setLoading(true);
      setError("");
      try {
         const data = await getMyConversation();
         setConversation(data);
      } catch (e) {
         setError(e?.message || "Nu s-a putut încărca conversația.");
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   useEffect(() => {
      setChatToastContext({
         popupOpen: true,
         conversationId: conversation?.id ?? null,
      });
   }, [conversation?.id]);

   useEffect(() => {
      const messages = conversation?.messages;
      if (!messages?.length) return;
      const last = messages[messages.length - 1];
      if (last?.id != null) setLastReadChatMessageId(last.id);
   }, [conversation?.messages]);

   useEffect(
      () => () => {
         setChatToastContext({ popupOpen: false, conversationId: null });
      },
      [],
   );

   useChatSocket(true, (message) => {
      if (!message || message.conversationId == null) return;
      setConversation((prev) => {
         if (!prev) return prev;
         if (Number(prev.id) !== Number(message.conversationId)) return prev;
         return {
            ...prev,
            messages: mergeMessageLists(prev.messages, [message]),
         };
      });
   });

   const handleSend = useCallback(
      async (body) => {
         setSending(true);
         setError("");
         try {
            const message = await postMyConversationMessage(body);
            setConversation((prev) => ({
               ...(prev || {}),
               id: prev?.id ?? message?.conversationId,
               messages: mergeMessageLists(prev?.messages, [message]),
            }));
            return true;
         } catch (e) {
            setError(e?.message || "Nu s-a putut trimite mesajul.");
            return false;
         } finally {
            setSending(false);
         }
      },
      [],
   );

   return (
      <ChatThreadBody
         head={<InstruireAutoChatProfile />}
         error={error}
         loading={loading}
         messages={conversation?.messages || []}
         viewerRole={user?.role}
         userId={user?.id}
         composer={
            <ChatComposer
               disabled={loading}
               sending={sending}
               onSend={handleSend}
               placeholder="Scrie un mesaj managerului..."
            />
         }
      />
   );
}

function StaffChatInboxView({
   onConversationChange,
   initialConversationId = null,
   initialStudent = null,
}) {
   const { user } = useContext(UserContext);
   const isMobile = useIsMobile();
   const pageSize = isMobile ? PAGE_SIZE_MOBILE : PAGE_SIZE;
   const isManagerRole = normalizeRole(user?.role) === ROLES.MANAGER;
   const restrictToMyGroups = isManagerRole;

   const workspace = useSyncExternalStore(
      subscribeStaffChatWorkspace,
      getStaffChatWorkspaceState,
      getStaffChatWorkspaceState,
   );

   const [students, setStudents] = useState([]);
   const [conversations, setConversations] = useState([]);
   const [groups, setGroups] = useState([]);
   // null = admin (fără restricție). Set = grupele atribuite managerului.
   const [managerGroupIds, setManagerGroupIds] = useState(null);
   const [thread, setThread] = useState(null);
   const [studentMetaById, setStudentMetaById] = useState({});
   const [loadingList, setLoadingList] = useState(true);
   const [loadingThread, setLoadingThread] = useState(false);
   const [error, setError] = useState("");
   const [sending, setSending] = useState(false);
   const [searchQuery, setSearchQuery] = useState("");
   const [visibleCount, setVisibleCount] = useState(pageSize);
   const [nowTick, setNowTick] = useState(() => Date.now());
   const selectedIdRef = useRef(null);
   const threadReqRef = useRef(0);

   const view = workspace.view === "thread" ? "thread" : "list";
   const activeTab =
      workspace.activeTab === "contacts" || workspace.activeTab === "groups"
         ? workspace.activeTab
         : "conversations";
   const selectedId = workspace.selectedConversationId;
   const selectedStudent = workspace.selectedStudent;
   const selectedStudentId =
      selectedStudent?.id != null ? Number(selectedStudent.id) : null;

   useEffect(() => {
      onConversationChange?.(view === "thread");
   }, [view, onConversationChange]);

   useEffect(() => {
      setChatToastContext({
         popupOpen: workspace.isOpen && workspace.dockMode === "expanded",
         conversationId: view === "thread" ? selectedId : null,
      });
   }, [view, selectedId, workspace.isOpen, workspace.dockMode]);

   useEffect(() => {
      if (initialConversationId == null && !initialStudent?.id) return;
      setStaffChatSelection({
         view: "thread",
         conversationId:
            initialConversationId != null ? initialConversationId : null,
         student: initialStudent || null,
      });
   }, [initialConversationId, initialStudent]);

   useEffect(() => {
      selectedIdRef.current = selectedId;
   }, [selectedId]);

   useEffect(() => {
      setVisibleCount(pageSize);
   }, [searchQuery, students.length, pageSize, activeTab]);

   const loadStudentsAndMeta = useCallback(async () => {
      try {
         // Users and groups are fetched independently: a groups failure must
         // not wipe the whole contacts list (that produced "both tabs empty").
         const users = unwrapList(await getUsers());

         let groups = [];
         try {
            groups = await getGroups();
         } catch {
            groups = [];
         }

         const groupNameById = {};
         for (const group of groups || []) {
            if (group?.id != null) {
               groupNameById[String(group.id)] =
                  safeText(group.name) || `Grupa ${group.id}`;
            }
         }

         const studentList = [];
         const map = {};
         for (const student of users || []) {
            if (!student?.id) continue;
            const role = normalizeRole(student.role);
            if (role !== ROLES.USER) continue;
            studentList.push(student);
            const groupId = readStudentGroupId(student);
            map[String(student.id)] = {
               phone: safeText(student.phone || student.phoneNumber),
               groupName: groupId
                  ? groupNameById[String(groupId)] || `Grupa ${groupId}`
                  : "Fără grupă",
            };
         }

         studentList.sort((a, b) =>
            readStudentName(a).localeCompare(readStudentName(b), "ro"),
         );
         setStudents(studentList);
         setStudentMetaById(map);
      } catch {
         // optional enrichment
      }
   }, []);

   const loadGroups = useCallback(async () => {
      if (!user?.id) {
         setGroups([]);
         setManagerGroupIds(restrictToMyGroups ? new Set() : null);
         return;
      }
      try {
         if (restrictToMyGroups) {
            const arr = await getManagerGroups(user.id);
            setGroups(arr);
            setManagerGroupIds(
               new Set(
                  arr
                     .map((g) => String(g?.id ?? g?.groupId ?? ""))
                     .filter(Boolean),
               ),
            );
         } else {
            const arr = await getGroups();
            setGroups(arr);
            setManagerGroupIds(null);
         }
      } catch {
         setGroups([]);
         setManagerGroupIds(restrictToMyGroups ? new Set() : null);
      }
   }, [user?.id, restrictToMyGroups]);

   const loadList = useCallback(async () => {
      setLoadingList(true);
      setError("");
      try {
         const data = await listStaffConversations();
         const sorted = Array.isArray(data)
            ? data.slice().sort(
                 (a, b) =>
                    new Date(
                       b.updatedAt || b.lastMessage?.createdAt || 0,
                    ).getTime() -
                    new Date(
                       a.updatedAt || a.lastMessage?.createdAt || 0,
                    ).getTime(),
              )
            : [];
         setConversations(sorted);
         syncStaffChatUnreadFromConversations(sorted, selectedIdRef.current);
      } catch (e) {
         setError(e?.message || "Nu s-au putut încărca conversațiile.");
      } finally {
         setLoadingList(false);
      }
   }, []);

   const loadThread = useCallback(async (id) => {
      if (id == null) return;
      const requestId = ++threadReqRef.current;
      const conversationId = Number(id);
      setLoadingThread(true);
      setError("");
      try {
         const data = await getStaffConversation(conversationId);
         if (requestId !== threadReqRef.current) return;
         setThread(data);
         if (data?.student?.id != null) {
            const current = getStaffChatWorkspaceState();
            if (
               Number(current.selectedStudent?.id) !== Number(data.student.id)
            ) {
               setStaffChatSelection({ student: data.student });
            }
         }
         const msgs = Array.isArray(data?.messages) ? data.messages : [];
         const lastMsg = msgs[msgs.length - 1] || data?.lastMessage;
         markConversationReadAndTrack(conversationId, lastMsg);
      } catch (e) {
         if (requestId !== threadReqRef.current) return;
         setError(e?.message || "Nu s-a putut încărca conversația.");
         setThread(null);
      } finally {
         if (requestId === threadReqRef.current) {
            setLoadingThread(false);
         }
      }
   }, []);

   useEffect(() => {
      loadStudentsAndMeta();
      loadGroups();
      loadList();
   }, [loadStudentsAndMeta, loadGroups, loadList]);

   useEffect(() => {
      if (!groups.length || !students.length) return;
      const nameById = {};
      for (const group of groups) {
         const id = group?.id ?? group?.groupId;
         if (id == null) continue;
         nameById[String(id)] = safeText(group.name) || `Grupa ${id}`;
      }
      setStudentMetaById((prev) => {
         let changed = false;
         const next = { ...prev };
         for (const student of students) {
            if (student?.id == null) continue;
            const gid = student.groupId ?? student.group?.id;
            if (gid == null || !nameById[String(gid)]) continue;
            const key = String(student.id);
            const current = next[key] || {};
            if (current.groupName === nameById[String(gid)]) continue;
            next[key] = { ...current, groupName: nameById[String(gid)] };
            changed = true;
         }
         return changed ? next : prev;
      });
   }, [groups, students]);

   // Re-evaluate „Răspunzi?” grace period while the list is open.
   useEffect(() => {
      if (view !== "list") return undefined;
      const id = window.setInterval(() => setNowTick(Date.now()), 60_000);
      return () => window.clearInterval(id);
   }, [view]);

   useEffect(() => {
      if (view !== "thread") return undefined;

      if (selectedId != null) {
         loadThread(selectedId);
         return undefined;
      }

      if (selectedStudentId != null) {
         // Deschiderea pe student (ex. „Răspunde" la o recenzie) nu aduce un
         // conversationId. Dacă exista deja o conversație cu elevul, o
         // selectăm: altfel firul s-ar deschide gol, istoricul ar părea
         // pierdut si am cere inutil un POST /chat/conversations.
         const existingId = (conversations || []).reduce((found, conv) => {
            if (found != null) return found;
            const studentId = readConversationStudentId(conv);
            if (studentId == null) return found;
            if (studentId !== Number(selectedStudentId)) return found;
            const convId = Number(conv?.id);
            return Number.isFinite(convId) && convId > 0 ? convId : found;
         }, null);

         if (existingId != null) {
            setStaffChatSelection({ conversationId: existingId });
            return undefined;
         }

         setThread({
            id: null,
            student: selectedStudent,
            messages: [],
         });
         setLoadingThread(false);
      }
      return undefined;
      // selectedStudent object identity must not retrigger loads
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [view, selectedId, selectedStudentId, loadThread, conversations]);

   useChatSocket(true, (message) => {
      if (!message?.conversationId) return;
      setConversations((prev) => {
         const list = Array.isArray(prev) ? prev.slice() : [];
         const idx = list.findIndex(
            (item) => Number(item.id) === Number(message.conversationId),
         );
         if (idx >= 0) {
            const current = list[idx];
            list[idx] = {
               ...current,
               updatedAt: message.createdAt || new Date().toISOString(),
               lastMessage: message,
            };
            list.sort(
               (a, b) =>
                  new Date(b.updatedAt).getTime() -
                  new Date(a.updatedAt).getTime(),
            );
            return list;
         }
         loadList();
         return prev;
      });

      if (Number(message.conversationId) === Number(selectedIdRef.current)) {
         // Viewing this thread — treat as read (survives refresh).
         markConversationReadAndTrack(message.conversationId, message);
      } else if (isStudentMessage(message)) {
         clearStaffChatAwaitingReply(message.conversationId);
         incrementStaffChatUnread(message.conversationId, 1);
      } else if (isStaffMessage(message)) {
         clearStaffChatAwaitingReply(message.conversationId);
      }

      setThread((prev) => {
         if (!prev || Number(prev.id) !== Number(message.conversationId)) {
            return prev;
         }
         return {
            ...prev,
            messages: mergeMessageLists(prev.messages, [message]),
         };
      });
   });

   const studentGroupById = useMemo(() => {
      const map = new Map();
      for (const student of students || []) {
         if (student?.id == null) continue;
         const gid = readStudentGroupId(student);
         map.set(String(student.id), gid != null ? String(gid) : null);
      }
      return map;
   }, [students]);

   const getRowGroupId = useCallback(
      (student) => {
         const direct = readStudentGroupId(student);
         if (direct != null) return String(direct);
         const fromMap = studentGroupById.get(String(student?.id));
         return fromMap != null ? String(fromMap) : null;
      },
      [studentGroupById],
   );

   const inMyGroups = useCallback(
      (student) => {
         if (!restrictToMyGroups) return false;
         if (!managerGroupIds || managerGroupIds.size === 0) return false;
         const gid = getRowGroupId(student);
         return gid != null && managerGroupIds.has(String(gid));
      },
      [getRowGroupId, managerGroupIds, restrictToMyGroups],
   );

   const isInManagerScope = useCallback(
      (student) => {
         if (!restrictToMyGroups) return true;
         return inMyGroups(student);
      },
      [restrictToMyGroups, inMyGroups],
   );

   const studentsById = useMemo(() => {
      const map = new Map();
      for (const student of students || []) {
         if (student?.id != null) map.set(String(student.id), student);
      }
      return map;
   }, [students]);

   const resolveConversationStudent = useCallback(
      (conv) => {
         const nested = conv?.student;
         if (nested?.id != null) {
            const fromList = studentsById.get(String(nested.id));
            return fromList ? { ...fromList, ...nested } : nested;
         }
         const sid = conv?.studentId ?? conv?.student_id;
         if (sid != null) {
            const fromList = studentsById.get(String(sid));
            return fromList ?? { id: sid };
         }
         return null;
      },
      [studentsById],
   );

   const conversationHasMessages = useCallback((conv) => {
      if (conv?.lastMessage?.body || conv?.lastMessage?.id) return true;
      if (Number(conv?.messageCount) > 0) return true;
      return Array.isArray(conv?.messages) && conv.messages.length > 0;
   }, []);

   const conversationRows = useMemo(() => {
      const now = nowTick;
      const prioritizeMyGroups =
         restrictToMyGroups && managerGroupIds && managerGroupIds.size > 0;
      return (conversations || [])
         .map((conv) => {
            if (!conversationHasMessages(conv)) return null;
            const student = resolveConversationStudent(conv);
            if (!student?.id) return null;
            const lastBody = safeText(conv?.lastMessage?.body);
            const updatedAt =
               conv?.updatedAt || conv?.lastMessage?.createdAt || null;
            const conversationId = conv?.id ?? null;
            const unreadCount =
               conversationId != null
                  ? getStaffChatUnreadCount(conversationId)
                  : 0;
            const awaiting = getStaffChatAwaitingReplyState(conversationId, {
               lastMessage: conv.lastMessage,
               unreadCount,
               now,
            });
            return {
               student,
               conversationId,
               lastMessage: conv?.lastMessage ?? null,
               updatedAt,
               preview: lastBody || "Niciun mesaj încă",
               avatarColor:
                  getAvatarColorFromName(student) || "hsl(210 90% 84%)",
               initials: getInitials(student),
               unreadCount,
               awaitingReply: awaiting.show,
               awaitingAgeMs: awaiting.ageMs,
               inMyGroup: inMyGroups(student),
            };
         })
         .filter(Boolean)
         .sort((a, b) => {
            if (prioritizeMyGroups) {
               const inA = a.inMyGroup ? 1 : 0;
               const inB = b.inMyGroup ? 1 : 0;
               if (inB !== inA) return inB - inA;
            }

            const unreadA = a.unreadCount > 0 ? 1 : 0;
            const unreadB = b.unreadCount > 0 ? 1 : 0;
            if (unreadB !== unreadA) return unreadB - unreadA;

            const awaitA = a.awaitingReply ? 1 : 0;
            const awaitB = b.awaitingReply ? 1 : 0;
            if (awaitB !== awaitA) return awaitB - awaitA;
            if (awaitA && awaitB) {
               return (b.awaitingAgeMs || 0) - (a.awaitingAgeMs || 0);
            }

            const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
            const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
            if (tb !== ta) return tb - ta;
            return readStudentName(a.student).localeCompare(
               readStudentName(b.student),
               "ro",
            );
         });
   }, [
      conversations,
      nowTick,
      workspace.awaitingReplyVersion,
      workspace.unreadByConversationId,
      managerGroupIds,
      inMyGroups,
      restrictToMyGroups,
      conversationHasMessages,
      resolveConversationStudent,
   ]);

   const contactRows = useMemo(() => {
      const convByStudentId = new Map();
      for (const conv of conversations || []) {
         const student = resolveConversationStudent(conv);
         const studentId = student?.id ?? conv?.studentId;
         if (studentId != null) convByStudentId.set(String(studentId), conv);
      }

      return (students || [])
         .map((student) => {
            const conv = convByStudentId.get(String(student.id));
            const mergedStudent = conv?.student
               ? { ...student, ...conv.student }
               : student;
            const lastBody = safeText(conv?.lastMessage?.body);
            const updatedAt =
               conv?.updatedAt || conv?.lastMessage?.createdAt || null;
            return {
               student: mergedStudent,
               conversationId: conv?.id ?? null,
               lastMessage: conv?.lastMessage ?? null,
               updatedAt,
               preview: lastBody || "Niciun mesaj încă",
               avatarColor:
                  getAvatarColorFromName(mergedStudent) || "hsl(210 90% 84%)",
               initials: getInitials(mergedStudent),
            };
         })
         .sort((a, b) =>
            readStudentName(a.student).localeCompare(
               readStudentName(b.student),
               "ro",
            ),
         );
   }, [students, conversations, resolveConversationStudent]);

   const query = useMemo(
      () => searchQuery.trim().toLowerCase(),
      [searchQuery],
   );

   const sourceRows = useMemo(() => {
      if (activeTab === "groups") {
         return contactRows.filter((row) => inMyGroups(row.student));
      }
      if (activeTab === "contacts") {
         return contactRows.filter((row) => isInManagerScope(row.student));
      }
      return conversationRows.filter((row) => isInManagerScope(row.student));
   }, [
      activeTab,
      contactRows,
      conversationRows,
      inMyGroups,
      isInManagerScope,
   ]);

   const filteredRows = useMemo(() => {
      if (!query) return sourceRows;
      return sourceRows.filter((row) => {
         const meta = studentMetaById[String(row.student?.id)] || {};
         const haystack = [
            readStudentName(row.student),
            row.student?.email,
            meta.phone,
            meta.groupName,
            row.preview,
         ]
            .map((part) => safeText(part).toLowerCase())
            .join(" ");
         return haystack.includes(query);
      });
   }, [sourceRows, query, studentMetaById]);

   const visibleRows = useMemo(
      () => filteredRows.slice(0, visibleCount),
      [filteredRows, visibleCount],
   );

   const totalResults = filteredRows.length;
   const canLoadMore = visibleCount < totalResults;
   const remaining = Math.max(0, totalResults - visibleCount);
   const nextBatch = Math.min(pageSize, remaining);

   // Cand firul e deschis pe un student „subtire" (doar id, venit dintr-o
   // recenzie fara `user` embedat), completam numele din lista incarcata.
   const activeStudentBase = selectedStudent || thread?.student;
   const activeStudent = (() => {
      if (activeStudentBase?.id == null) return activeStudentBase;
      const fromList = studentsById.get(String(activeStudentBase.id));
      if (!fromList) return activeStudentBase;
      return { ...fromList, ...activeStudentBase };
   })();
   const activeMeta = studentMetaById[String(activeStudent?.id)] || {};
   const activeColor =
      getAvatarColorFromName(activeStudent) || "hsl(210 90% 84%)";

   const handleSelectRow = useCallback(
      (row) => {
         const conversationId =
            row.conversationId != null ? Number(row.conversationId) : null;
         setStaffChatSelection({
            view: "thread",
            student: row.student,
            conversationId:
               Number.isFinite(conversationId) && conversationId > 0
                  ? conversationId
                  : null,
         });
         if (Number.isFinite(conversationId) && conversationId > 0) {
            // Load immediately on click (don't wait only on effect).
            loadThread(conversationId);
            markConversationReadAndTrack(conversationId, row.lastMessage);
         } else {
            threadReqRef.current += 1;
            setLoadingThread(false);
            setThread({
               id: null,
               student: row.student,
               messages: [],
            });
         }
      },
      [loadThread],
   );

   const handleBackToList = useCallback(() => {
      clearStaffChatThreadSelection();
      setThread(null);
      setError("");
   }, []);

   const handleLoadMore = () => {
      setVisibleCount((count) => Math.min(count + pageSize, totalResults));
   };

   const handleSend = useCallback(
      async (body) => {
         setSending(true);
         setError("");
         try {
            let conversationId = selectedId;
            if (conversationId == null && selectedStudent?.id != null) {
               const created = await ensureStaffConversation(selectedStudent.id);
               conversationId = created?.id ?? null;
               if (conversationId != null) {
                  setStaffChatSelection({ conversationId });
                  await loadList();
               }
            }
            if (conversationId == null) {
               throw new Error("Nu s-a putut deschide conversația.");
            }

            const message = await postStaffConversationMessage(
               conversationId,
               body,
            );
            clearStaffChatAwaitingReply(conversationId);
            setThread((prev) =>
               prev
                  ? {
                       ...prev,
                       id: conversationId,
                       messages: mergeMessageLists(prev.messages, [message]),
                    }
                  : {
                       id: conversationId,
                       student: selectedStudent,
                       messages: [message],
                    },
            );
            await loadList();
            return true;
         } catch (e) {
            setError(e?.message || "Nu s-a putut trimite mesajul.");
            return false;
         } finally {
            setSending(false);
         }
      },
      [selectedId, selectedStudent, loadList],
   );

   if (view === "list") {
      const scopedConversations = restrictToMyGroups
         ? (conversations || []).filter((conv) => {
              const student = resolveConversationStudent(conv);
              return student && isInManagerScope(student);
           })
         : conversations;
      const awaitingCount = countStaffChatAwaitingReply(
         scopedConversations,
         nowTick,
      );

      return (
         <div className="staffChatPopup__listView">
            <div className="staffChatPopup__tabs" role="tablist">
               <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "groups"}
                  className={
                     "staffChatPopup__tab" +
                     (activeTab === "groups" ? " is-active" : "")
                  }
                  onClick={() => setStaffChatActiveTab("groups")}
               >
                  Grupele mele
               </button>
               <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "conversations"}
                  className={
                     "staffChatPopup__tab" +
                     (activeTab === "conversations" ? " is-active" : "") +
                     (awaitingCount > 0 ? " has-awaiting" : "")
                  }
                  onClick={() => setStaffChatActiveTab("conversations")}
               >
                  <span className="staffChatPopup__tabLabel">Conversații</span>
                  {awaitingCount > 0 ? (
                     <span
                        className="staffChatPopup__tabAwaitingDot"
                        aria-label={`${awaitingCount} conversații fără răspuns`}
                        title={`${awaitingCount} fără răspuns`}
                     />
                  ) : null}
               </button>
               <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === "contacts"}
                  className={
                     "staffChatPopup__tab" +
                     (activeTab === "contacts" ? " is-active" : "")
                  }
                  onClick={() => setStaffChatActiveTab("contacts")}
               >
                  Studenți
               </button>
            </div>

            <div className="staffChatPopup__listHeader">
               <input
                  type="search"
                  className="staffChatPopup__searchInput"
                  placeholder={
                     activeTab === "groups"
                        ? "Caută student din grupe..."
                        : activeTab === "contacts"
                          ? restrictToMyGroups
                             ? "Caută student din grupe..."
                             : "Caută student..."
                          : "Caută conversație..."
                  }
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
               />
            </div>

            <div className="staffChatPopup__gridWrap">
               <div className="staffChatPopup__grid">
                  {loadingList &&
                  ((activeTab === "conversations" && !conversations.length) ||
                     (activeTab !== "conversations" &&
                        !students.length &&
                        !conversations.length)) ? (
                     <p className="staffChatPopup__state">Se încarcă...</p>
                  ) : null}

                  {!loadingList &&
                     visibleRows.map((row) => {
                        const unreadCount =
                           row.unreadCount != null
                              ? row.unreadCount
                              : row.conversationId != null
                                ? getStaffChatUnreadCount(row.conversationId)
                                : 0;
                        const showAwaiting =
                           activeTab === "conversations" &&
                           row.awaitingReply &&
                           unreadCount <= 0;
                        const meta =
                           studentMetaById[String(row.student?.id)] || {};
                        const groupLabel = meta.groupName || "Fără grupă";
                        const secondaryText =
                           activeTab === "conversations"
                              ? row.inMyGroup
                                 ? `${row.preview} · ${groupLabel}`
                                 : row.preview
                              : groupLabel;
                        return (
                           <StudentItem
                              key={row.student.id}
                              student={row.student}
                              color={row.avatarColor}
                              initials={row.initials}
                              onOpen={() => handleSelectRow(row)}
                              secondaryText={secondaryText}
                              highlightQuery={searchQuery}
                              highlightClassName="studentItem__highlight"
                              showChevron={false}
                              endAdornment={
                                 <span className="staffChatPopup__rowEnd">
                                    {showAwaiting ? (
                                       <AwaitingReplyPill
                                          conversationId={row.conversationId}
                                          messageId={row.lastMessage?.id}
                                       />
                                    ) : null}
                                    {activeTab === "conversations" ? (
                                       <span
                                          className={
                                             "staffChatPopup__unreadBadge" +
                                             (unreadCount > 0
                                                ? ""
                                                : " staffChatPopup__unreadBadge--zero")
                                          }
                                          aria-label={`${unreadCount} mesaje necitite`}
                                       >
                                          {unreadCount > 99
                                             ? "99+"
                                             : unreadCount}
                                       </span>
                                    ) : null}
                                 </span>
                              }
                              className="staffChatPopup__listRow"
                           />
                        );
                     })}

                  {!loadingList && totalResults > 0 ? (
                     <div className="staffChatPopup__listFooter">
                        <span className="staffChatPopup__counter">
                           Afișate {Math.min(visibleCount, totalResults)} din{" "}
                           {totalResults}
                        </span>
                        {canLoadMore ? (
                           <button
                              type="button"
                              className="staffChatPopup__loadMore"
                              onClick={handleLoadMore}
                           >
                              Afișează încă {nextBatch}
                           </button>
                        ) : null}
                     </div>
                  ) : null}

                  {!loadingList && totalResults === 0 ? (
                     <p className="staffChatPopup__empty">
                        {activeTab === "conversations"
                           ? "Nicio conversație cu mesaje încă."
                           : activeTab === "groups" || restrictToMyGroups
                             ? "Niciun student în grupele tale."
                             : "Nu s-au găsit studenți."}
                     </p>
                  ) : null}
               </div>
            </div>
         </div>
      );
   }

   return (
      <ChatThreadBody
         head={
            <>
               <IconButton
                  icon="arrow"
                  variant="pill"
                  className="staffChatPopup__back"
                  onClick={handleBackToList}
                  aria-label="Înapoi la listă"
               />
               {activeStudent ? (
                  <StudentItemInline
                     student={activeStudent}
                     color={activeColor}
                     className="staffChatPopup__threadProfile"
                     metaLeft={activeMeta.groupName || "Fără grupă"}
                     secondaryText={activeMeta.phone || ""}
                     showIdnp={false}
                     showPhoneCopy
                  />
               ) : (
                  <InstruireAutoChatProfile />
               )}
            </>
         }
         headClassName="staffChatPopup__threadHead--staff"
         error={error}
         loading={loadingThread && selectedId != null}
         messages={thread?.messages || []}
         viewerRole={user?.role}
         userId={user?.id}
         composer={
            <ChatComposer
               disabled={loadingThread && selectedId != null}
               sending={sending}
               onSend={handleSend}
               placeholder="Scrie un mesaj..."
               draftText={workspace.composerDraft}
               draftToken={workspace.composerDraftToken}
            />
         }
      />
   );
}

/**
 * Firul de mesaje cu un singur elev, încorporat în profilul lui (tabul
 * „Mesaje"). Caută conversația existentă; dacă nu există, o creează abia la
 * primul mesaj trimis, ca simpla deschidere a tabului să nu lase conversații
 * goale în inbox.
 */
export function StudentProfileChat({ student }) {
   const { user } = useContext(UserContext);
   const studentId = Number(student?.id);
   const [conversationId, setConversationId] = useState(null);
   const [messages, setMessages] = useState([]);
   const [loading, setLoading] = useState(true);
   const [sending, setSending] = useState(false);
   const [error, setError] = useState("");
   const conversationIdRef = useRef(null);

   useEffect(() => {
      conversationIdRef.current = conversationId;
   }, [conversationId]);

   useEffect(() => {
      if (!Number.isFinite(studentId) || studentId <= 0) {
         setLoading(false);
         return undefined;
      }
      let cancelled = false;
      setLoading(true);
      setError("");
      setConversationId(null);
      setMessages([]);

      (async () => {
         try {
            const list = await listStaffConversations();
            const existing = (list || []).find(
               (conv) => readConversationStudentId(conv) === studentId,
            );
            if (cancelled || !existing) return;
            const data = await getStaffConversation(existing.id);
            if (cancelled) return;
            const msgs = Array.isArray(data?.messages) ? data.messages : [];
            setConversationId(Number(existing.id));
            setMessages(msgs);
            markConversationReadAndTrack(
               existing.id,
               msgs[msgs.length - 1] || data?.lastMessage,
            );
         } catch (e) {
            if (!cancelled) {
               setError(e?.message || "Nu s-a putut încărca conversația.");
            }
         } finally {
            if (!cancelled) setLoading(false);
         }
      })();

      return () => {
         cancelled = true;
      };
   }, [studentId]);

   useChatSocket(true, (message) => {
      if (!message?.conversationId) return;
      if (Number(message.conversationId) !== Number(conversationIdRef.current)) {
         return;
      }
      setMessages((prev) => mergeMessageLists(prev, [message]));
      markConversationReadAndTrack(message.conversationId, message);
   });

   const handleSend = useCallback(
      async (body) => {
         setSending(true);
         setError("");
         try {
            let id = conversationIdRef.current;
            if (id == null) {
               const created = await ensureStaffConversation(studentId);
               id = created?.id ?? null;
               if (id == null) {
                  throw new Error("Nu s-a putut deschide conversația.");
               }
               setConversationId(Number(id));
               conversationIdRef.current = Number(id);
               if (Array.isArray(created?.messages) && created.messages.length) {
                  setMessages((prev) => mergeMessageLists(prev, created.messages));
               }
            }
            const message = await postStaffConversationMessage(id, body);
            clearStaffChatAwaitingReply(id);
            setMessages((prev) => mergeMessageLists(prev, [message]));
            return true;
         } catch (e) {
            setError(e?.message || "Nu s-a putut trimite mesajul.");
            return false;
         } finally {
            setSending(false);
         }
      },
      [studentId],
   );

   return (
      <div className="staffChatPopup staffChatPopup--embedded">
         <ChatThreadBody
            head={null}
            error={error}
            loading={loading}
            messages={messages}
            viewerRole={user?.role}
            userId={user?.id}
            composer={
               <ChatComposer
                  disabled={loading}
                  sending={sending}
                  onSend={handleSend}
                  placeholder="Scrie un mesaj..."
               />
            }
         />
      </div>
   );
}

export default function StaffChatPopup({
   conversationId = null,
   student = null,
   openThread = false,
}) {
   const { user } = useContext(UserContext);
   const staff = isStaffRole(user?.role);
   const workspace = useSyncExternalStore(
      subscribeStaffChatWorkspace,
      getStaffChatWorkspaceState,
      getStaffChatWorkspaceState,
   );
   const unreadTotal = getStaffChatUnreadTotal();
   // Staff list/thread chrome must follow workspace.view only.
   // openThread is a one-shot open hint — if it stays in the class check,
   // going "back" keeps --conversation (padding:0) and sticks content to edges.
   const inConversation = !staff || workspace.view === "thread";

   useEffect(() => {
      if (openThread) {
         setStaffChatView("thread");
      }
   }, [openThread]);

   useEffect(
      () => () => {
         // Do not wipe workspace selection on unmount — only toast context.
         setChatToastContext({ popupOpen: false, conversationId: null });
      },
      [],
   );

   const titleBase = staff ? "Mesaje" : "Mesaje";
   const title =
      staff && unreadTotal > 0 ? `${titleBase} (${unreadTotal})` : titleBase;

   return (
      <div
         className={
            "staffChatPopup" +
            (inConversation ? " staffChatPopup--conversation" : "")
         }
      >
         <div className="staffChatPopup__header">
            <h3 className="staffChatPopup__title">{title}</h3>
         </div>
         {staff ? (
            <StaffChatInboxView
               onConversationChange={() => {}}
               initialConversationId={openThread ? conversationId : null}
               initialStudent={openThread ? student : null}
            />
         ) : (
            <StudentChatView />
         )}
      </div>
   );
}

